/** /review annotation console API client (阶段②). Carries the Bearer token. */
import { authHeaders } from "./auth";
import type { BatchAgreement, MultiAnnotatedRecord, ReviewAction, ReviewCounts, ReviewDetailView, ReviewQueueItem, ReviewSeedRecord, TriageStatus } from "@shared/types";
import type { ReviewDraft } from "../review/draft";

function qs(params: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") parts.push(`${k}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

export interface ReviewQueueFilter {
  signal?: "down" | "handoff" | "batch";
  status?: TriageStatus;
  /** "mine" = records the caller hasn't annotated yet (per-reviewer pending view). */
  pending?: "mine";
  /** "1" = records with >=2 human annotations. */
  multi?: "1";
  /** "1" = records whose human verdicts disagree. */
  disagreement?: "1";
}

export async function fetchReviewQueue(f: ReviewQueueFilter = {}): Promise<ReviewQueueItem[]> {
  const body = await getJson<{ items: ReviewQueueItem[] }>(
    `/api/review/queue${qs({ signal: f.signal, status: f.status, pending: f.pending, multi: f.multi, disagreement: f.disagreement })}`
  );
  return body.items;
}

/** Per-chip counts for the unified filter bar. */
export async function fetchReviewCounts(): Promise<ReviewCounts> {
  const body = await getJson<{ counts: ReviewCounts }>("/api/review/counts");
  return body.counts;
}

export async function fetchReviewDetail(annotationId: number): Promise<ReviewDetailView> {
  const body = await getJson<{ detail: ReviewDetailView }>(`/api/review/${annotationId}`);
  return body.detail;
}

export async function fetchReviewDraft(annotationId: number): Promise<ReviewDraft | null> {
  const body = await getJson<{ draft: ReviewDraft | null }>(`/api/review/${annotationId}/draft`);
  return body.draft;
}

/** List saved gold seeds (the 种子产出 view). */
export async function fetchReviewSeeds(): Promise<ReviewSeedRecord[]> {
  const body = await getJson<{ seeds: ReviewSeedRecord[] }>("/api/review/seeds");
  return body.seeds;
}

export interface ResolveInput {
  action: ReviewAction;
  after?: string;
  reasons?: string[];
  impliedClause?: string;
  mustCite?: string[];
  keyPoints?: string[];
  judge?: "contains" | "cite_required" | "must_refuse" | "rubric";
}

export interface ResolveResult {
  ok: boolean;
  action: ReviewAction;
  gold?: ReviewSeedRecord;
}

export async function resolveReview(annotationId: number, input: ResolveInput): Promise<ResolveResult> {
  return postJson<ResolveResult>(`/api/review/${annotationId}/resolve`, input);
}

export interface AgreementResponse {
  agreement: BatchAgreement;
  disagreements: { responseId: string; verdicts: string[]; agreementPct: number | null }[];
}
/** Admin-only. Throws HTTP 403 for non-admins. */
export async function fetchAgreement(): Promise<AgreementResponse> {
  return getJson<AgreementResponse>("/api/review/agreement");
}
export async function setCanonical(responseId: string, gold: string): Promise<void> {
  await postJson<{ ok: boolean }>("/api/review/canonical", { responseId, gold });
}

/** Admin-only. All multi-annotated records with every annotation + current canonical. */
export async function fetchMultiAnnotated(): Promise<MultiAnnotatedRecord[]> {
  const body = await getJson<{ records: MultiAnnotatedRecord[] }>("/api/review/multi");
  return body.records;
}

/** On-demand LLM rewrite of the annotator's draft. Returns null if the LLM is unavailable. */
export async function rewriteDraft(annotationId: number, text: string): Promise<string | null> {
  const body = await postJson<{ rewritten: string | null }>(`/api/review/${annotationId}/rewrite`, { text });
  return body.rewritten;
}
